// 節點分頁（NODE-01，SPEC #node-93）：Node Host（KST 兼任或 kinio-node-host）底下的 Kinio Node 板子。
// 清單照 shadow kinio_node_host.nodes：名稱 = board + node-id 尾碼、健康四態、韌體、序列埠、最後回應。
// 節點有報 aid、且那台在同家戶看得到 → 連到節點自己的詳細頁（在線、事件）；其他只列不連。
// 節點是非影像設備：不進影像牆、不算攝影機（AID 是 GEN，lib/devices isCamera 本來就不算）。
// 斷電 / 重設 / 燒錄（NODE-02）走 fleet API、控制端是持有 Host AID 的 org admin，不在這頁。
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { VmsDevice } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { useTzResolver } from '../../lib/useTz';
import { hostNodesOf, nodeLabel, nodeThing } from './nodes';
import { ttlSummary } from './ttlTarget';

const HEALTH_CLS: Record<string, string> = { healthy: 'badge-ok', busy: 'badge-warn', unhealthy: 'badge-err', unknown: '' };

export function HostNodes({ dev, reported, tree }: { dev: VmsDevice; reported?: Record<string, unknown>; tree: VmsDevice[] }) {
  const { t, i18n } = useTranslation();
  const tzOf = useTzResolver();
  const nodes = hostNodesOf(reported);
  if (!nodes) return null;
  return (
    <div data-testid="host-nodes">
      <p className="muted small">{t('hnodes.intro')}</p>
      {nodes.length === 0 ? (
        <p className="muted">{t('hnodes.none')}</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th scope="col">{t('hnodes.col_node')}</th>
                <th scope="col">{t('hnodes.col_health')}</th>
                <th scope="col">{t('hnodes.col_fw')}</th>
                <th scope="col">{t('hnodes.col_seen')}</th>
                <th scope="col">{t('hnodes.col_thing')}</th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((n) => {
                const th = nodeThing(dev, n, tree);
                return (
                  <tr key={n.id} data-node={n.id}>
                    <th scope="row">
                      {nodeLabel(n)}
                      <div className="small muted">
                        {[n.soc, n.port].filter(Boolean).join(' · ')}
                      </div>
                    </th>
                    <td>
                      <span className={`badge ${HEALTH_CLS[n.health]}`} data-health={n.health}>
                        {t(`hnodes.h_${n.health}`)}
                      </span>
                    </td>
                    <td className="small">{n.fw ?? <span className="muted">{t('hnodes.unknown')}</span>}</td>
                    <td className="small">
                      {n.lastSeen ? formatDateTime(n.lastSeen, tzOf(dev.homeId), i18n.language) : <span className="muted">{t('hnodes.unknown')}</span>}
                    </td>
                    <td className="small">
                      {th ? (
                        <>
                          <Link to={`/devices/${encodeURIComponent(th.homeId)}/${encodeURIComponent(th.aid)}`} data-testid="node-thing">
                            <span className={`dot ${th.presence}`} /> {th.name} · {t(`devices.${th.presence}`)}
                          </Link>
                          {/* 節點 UART1 接著哪台（NODE-06）：沒回報就不寫 */}
                          {ttlSummary(t, th, tree) && (
                            <div className="muted small" data-testid="node-uart">
                              {ttlSummary(t, th, tree)}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="muted" data-testid="node-unlinked">
                          {n.aid ? t('hnodes.not_visible') : t('hnodes.no_aid')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
